import { useState, useEffect } from 'react'
import {
  Loader2,
  ImagePlus,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Camera,
  Smile,
  BookOpen,
  Scale,
  LayoutGrid,
  Palette,
  Globe,
} from 'lucide-react'
import type { VisualTaskCreateRequest } from '../../lib/api'
import { useVisualTemplates } from '../../hooks/useVisualTemplates'
import { generateImagePrompt } from '../../lib/api'
import { toast } from 'sonner'
import { AspectRatioPreview } from './AspectRatioPreview'
import { ReferenceImageUploader } from './ReferenceImageUploader'
import { PromptHistoryPanel, pushPromptHistory } from './PromptHistoryPanel'
import type { PromptHistoryEntry } from './PromptHistoryPanel'
const CHANNELS = [
  { value: 'social', label: 'Social' },
  { value: 'blog', label: 'Blog' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'newsletter', label: 'Newsletter' },
]

interface ChannelDefaults {
  aspect_ratio: string
  image_size: string
}

const CHANNEL_DEFAULTS: Record<string, ChannelDefaults> = {
  linkedin: { aspect_ratio: '4:3', image_size: '1K' },
  instagram: { aspect_ratio: '1:1', image_size: '2K' },
  blog: { aspect_ratio: '16:9', image_size: '2K' },
  twitter: { aspect_ratio: '16:9', image_size: '1K' },
  facebook: { aspect_ratio: '16:9', image_size: '1K' },
  newsletter: { aspect_ratio: '1:1', image_size: '1K' },
  social: { aspect_ratio: '1:1', image_size: '2K' },
}

const STYLE_PRESETS = [
  { value: 'hiperrealista', label: 'Hiperrealista', icon: Camera, description: 'Fotorrealista' },
  { value: 'caricatura_americana', label: 'Caricatura Americana', icon: Smile, description: 'Estilo editorial US' },
  { value: 'manga', label: 'Manga', icon: BookOpen, description: 'Estilo japones' },
  { value: 'caricatura_politica', label: 'Caricatura Politica', icon: Scale, description: 'Satira editorial' },
  { value: 'carteles', label: 'Carteles', icon: LayoutGrid, description: 'Poster y afiches' },
  { value: 'arte_digital', label: 'Arte Digital', icon: Palette, description: 'Ilustracion digital' },
  { value: 'neural_cartography', label: 'Neural Cartography', icon: Globe, description: 'Mapas y datos' },
] as const

interface ModelOption {
  value: string
  label: string
  speed: 'rapido' | 'normal' | 'lento'
  quality: 'buena' | 'alta' | 'maxima'
  description: string
}

const MODEL_OPTIONS: ModelOption[] = [
  {
    value: '',
    label: 'Predeterminado',
    speed: 'normal',
    quality: 'alta',
    description: 'Seleccion automatica segun el canal',
  },
  {
    value: 'gemini3-flash',
    label: 'Gemini 3 Flash',
    speed: 'rapido',
    quality: 'buena',
    description: 'Generacion veloz; ideal para borradores y pruebas rapidas',
  },
  {
    value: 'gemini3',
    label: 'Gemini 3 Pro',
    speed: 'normal',
    quality: 'alta',
    description: 'Balance optimo entre velocidad y calidad visual',
  },
  {
    value: 'imagen4-fast',
    label: 'Imagen 4 Fast',
    speed: 'rapido',
    quality: 'alta',
    description: 'Alta fidelidad fotografica con latencia reducida',
  },
  {
    value: 'imagen4',
    label: 'Imagen 4',
    speed: 'normal',
    quality: 'maxima',
    description: 'Calidad de produccion; detalle y coherencia excepcionales',
  },
  {
    value: 'imagen4-ultra',
    label: 'Imagen 4 Ultra',
    speed: 'lento',
    quality: 'maxima',
    description: 'Maxima calidad posible; recomendado para assets finales',
  },
]

const SPEED_COLORS: Record<ModelOption['speed'], string> = {
  rapido: 'text-emerald-600 bg-emerald-50',
  normal: 'text-blue-600 bg-blue-50',
  lento: 'text-amber-600 bg-amber-50',
}

const QUALITY_COLORS: Record<ModelOption['quality'], string> = {
  buena: 'text-slate-600 bg-slate-100',
  alta: 'text-violet-600 bg-violet-50',
  maxima: 'text-orange-600 bg-orange-50',
}

const ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 (Cuadrado)' },
  { value: '16:9', label: '16:9 (Landscape)' },
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '3:2', label: '3:2' },
  { value: '2:3', label: '2:3' },
]

const IMAGE_SIZES = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
]

const THINKING_LEVELS = [
  { value: 'low', label: 'Bajo (rapido)' },
  { value: 'medium', label: 'Medio' },
  { value: 'high', label: 'Alto (mejor composicion)' },
  { value: 'minimal', label: 'Minimo' },
]

export interface VisualTaskFormData {
  content_excerpt: string
  canal: string
  template_id: string
}

interface VisualTaskFormProps {
  onSubmit: (data: VisualTaskCreateRequest) => Promise<void>
  isLoading?: boolean
  /** Pre-fill excerpt when opening from a content piece (e.g. ContentDetailPage). */
  initialContentExcerpt?: string
  /** When set, submit payload will include this content_id to link the visual task to the piece. */
  contentId?: string | null
  /** When set, new generation will use this image as reference (edit-from-image flow). */
  sourceImageId?: string | null
  /**
   * Compact mode hides verbose labels and collapses secondary controls into a
   * slide-out panel. Designed for the three-column workspace layout.
   */
  compact?: boolean
  /** Called when aspect ratio changes (for CanvasWorkspace sync in workspace layout). */
  onAspectRatioChange?: (ratio: string) => void
}

export function VisualTaskForm({ onSubmit, isLoading, initialContentExcerpt = '', contentId, sourceImageId, compact = false, onAspectRatioChange }: VisualTaskFormProps) {
  const [excerpt, setExcerpt] = useState(initialContentExcerpt)

  useEffect(() => {
    setExcerpt(initialContentExcerpt)
  }, [initialContentExcerpt])
  const [canal, setCanal] = useState('social')
  const [templateId, setTemplateId] = useState('')
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [numImages, setNumImages] = useState(1)
  const [preset, setPreset] = useState('neural_cartography')
  const [modelUsed, setModelUsed] = useState('')
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [imageSize, setImageSize] = useState('2K')
  const [thinkingLevel, setThinkingLevel] = useState('high')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [useGrounding, setUseGrounding] = useState(false)
  const [showReferenceImage, setShowReferenceImage] = useState(false)
  const [referenceImageId, setReferenceImageId] = useState<string | null>(null)
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null)
  const [showPromptGenerator, setShowPromptGenerator] = useState(false)
  const [promptTema, setPromptTema] = useState('')
  const [promptContexto, setPromptContexto] = useState('')
  const [promptEstilo, setPromptEstilo] = useState('neural_cartography')
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false)

  const { data: templates = [], isLoading: isLoadingTemplates } = useVisualTemplates({
    solo_activos: true,
  })

  const handleCanalChange = (value: string) => {
    setCanal(value)
    const defaults = CHANNEL_DEFAULTS[value]
    if (defaults) {
      setAspectRatio(defaults.aspect_ratio)
      setImageSize(defaults.image_size)
      onAspectRatioChange?.(defaults.aspect_ratio)
    }
  }

  const handleAspectRatioChange = (value: string) => {
    setAspectRatio(value)
    onAspectRatioChange?.(value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const resolvedSourceImageId = referenceImageId ?? sourceImageId ?? null
    await onSubmit({
      ...(contentId != null ? { content_id: contentId } : {}),
      ...(resolvedSourceImageId != null ? { source_image_id: resolvedSourceImageId } : {}),
      content_excerpt: excerpt.trim(),
      canal: canal || 'social',
      template_id: templateId.trim() || null,
      num_images: numImages,
      preset,
      ...(modelUsed.trim() ? { model_used: modelUsed.trim() } : {}),
      aspect_ratio: aspectRatio,
      image_size: imageSize,
      ...(showAdvancedOptions ? { thinking_level: thinkingLevel } : {}),
      ...(negativePrompt.trim() ? { negative_prompt: negativePrompt.trim() } : {}),
      use_grounding: useGrounding,
    })
    if (excerpt.trim()) {
      pushPromptHistory({ prompt: excerpt.trim(), canal, preset })
    }
    setExcerpt('')
    setTemplateId('')
  }

  const handleReusePrompt = (entry: PromptHistoryEntry) => {
    setExcerpt(entry.prompt)
    if (entry.canal) {
      handleCanalChange(entry.canal)
    }
    if (entry.preset) {
      setPreset(entry.preset)
    }
  }

  const handleGeneratePrompt = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!promptTema.trim()) return
    setIsGeneratingPrompt(true)
    try {
      const result = await generateImagePrompt({
        tema: promptTema.trim(),
        contexto: promptContexto.trim() || undefined,
        estilo: promptEstilo,
        canal,
      })
      setExcerpt(result.prompt)
      setShowPromptGenerator(false)
      setPromptTema('')
      setPromptContexto('')
      toast.success('Prompt generado', {
        description: `Estilo: ${result.estilo}, ratio: ${result.aspect_ratio}`,
      })
    } catch (err) {
      toast.error('Error al generar el prompt', {
        description: err instanceof Error ? err.message : 'Intenta de nuevo',
      })
    } finally {
      setIsGeneratingPrompt(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          {!compact && (
            <label htmlFor="visual-excerpt" className="label">
              Extracto o descripcion *
            </label>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <PromptHistoryPanel onSelect={handleReusePrompt} />
            <button
              type="button"
              onClick={() => setShowPromptGenerator((v) => !v)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              <Sparkles className="w-4 h-4" />
              {compact ? 'IA' : 'Generar prompt con IA'}
            </button>
          </div>
        </div>
        {showPromptGenerator && (
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <form onSubmit={handleGeneratePrompt} className="space-y-3">
              <div>
                <label htmlFor="prompt-tema" className="label text-sm">
                  Tema *
                </label>
                <input
                  id="prompt-tema"
                  type="text"
                  className="input"
                  placeholder="Ej. estrategia de negocios, liderazgo"
                  value={promptTema}
                  onChange={(e) => setPromptTema(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="prompt-contexto" className="label text-sm">
                  Contexto (opcional)
                </label>
                <textarea
                  id="prompt-contexto"
                  className="input min-h-[60px]"
                  rows={2}
                  placeholder="Contexto adicional para el prompt"
                  value={promptContexto}
                  onChange={(e) => setPromptContexto(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="prompt-estilo" className="label text-sm">
                  Estilo
                </label>
                <select
                  id="prompt-estilo"
                  className="input"
                  value={promptEstilo}
                  onChange={(e) => setPromptEstilo(e.target.value)}
                >
                  {STYLE_PRESETS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={!promptTema.trim() || isGeneratingPrompt}
                  className="btn btn-primary text-sm flex items-center gap-2"
                >
                  {isGeneratingPrompt ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {isGeneratingPrompt ? 'Generando...' : 'Generar y usar'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPromptGenerator(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cerrar
                </button>
              </div>
            </form>
          </div>
        )}
        <textarea
          id="visual-excerpt"
          className={compact ? 'input min-h-[70px]' : 'input min-h-[100px]'}
          rows={compact ? 3 : 4}
          placeholder="Texto o descripcion para generar la imagen..."
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          required
        />
      </div>

      <div>
        <label className={compact ? 'label text-xs mb-1 block' : 'label mb-2 block'}>Estilo visual</label>
        {compact ? (
          <select
            className="input"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
          >
            {STYLE_PRESETS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label} — {s.description}
              </option>
            ))}
          </select>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {STYLE_PRESETS.map((s) => {
              const Icon = s.icon
              const isSelected = preset === s.value
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setPreset(s.value)}
                  className={`
                    flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 text-left transition-colors
                    ${isSelected
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}
                  `}
                >
                  <span
                    className={`
                      flex h-10 w-10 items-center justify-center rounded-lg
                      ${isSelected ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-600'}
                    `}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-medium leading-tight">{s.label}</span>
                  {s.description && (
                    <span className="text-[10px] text-slate-500 leading-tight line-clamp-1">
                      {s.description}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="visual-canal" className="label">
            Canal
          </label>
          <select
            id="visual-canal"
            className="input"
            value={canal}
            onChange={(e) => handleCanalChange(e.target.value)}
          >
            {CHANNELS.map((ch) => (
              <option key={ch.value} value={ch.value}>
                {ch.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="visual-template" className="label">
            Plantilla (opcional)
          </label>
          <select
            id="visual-template"
            className="input"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={isLoadingTemplates}
          >
            <option value="">Ninguna (predeterminada)</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.category ? ` (${t.category})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="visual-aspect-ratio" className="label">
            Proporcion (aspect ratio)
          </label>
          <select
            id="visual-aspect-ratio"
            className="input"
            value={aspectRatio}
            onChange={(e) => handleAspectRatioChange(e.target.value)}
          >
            {ASPECT_RATIOS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="visual-num-images" className="label">
            Numero de imagenes
          </label>
          <select
            id="visual-num-images"
            className="input"
            value={numImages}
            onChange={(e) => setNumImages(Number(e.target.value))}
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!compact && (
        <div className="flex items-center justify-between gap-4 px-3 py-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="text-xs text-slate-500 space-y-0.5">
            <p className="font-medium text-slate-700">Canvas preview</p>
            <p>Cambia el canal para actualizar automaticamente</p>
          </div>
          <AspectRatioPreview aspectRatio={aspectRatio} maxWidth={120} maxHeight={90} />
        </div>
      )}

      <div>
        <label className={compact ? 'label text-xs mb-1 block' : 'label mb-2 block'}>Modelo</label>
        {compact ? (
          <select
            className="input"
            value={modelUsed}
            onChange={(e) => setModelUsed(e.target.value)}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.value || 'default'} value={m.value}>
                {m.label} ({m.speed}, {m.quality})
              </option>
            ))}
          </select>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {MODEL_OPTIONS.map((m) => {
              const isSelected = modelUsed === m.value
              return (
                <button
                  key={m.value || 'default'}
                  type="button"
                  onClick={() => setModelUsed(m.value)}
                  className={`
                    flex flex-col gap-1.5 p-3 rounded-lg border-2 text-left transition-colors
                    ${isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}
                  `}
                >
                  <span className={`text-sm font-semibold ${isSelected ? 'text-emerald-800' : 'text-slate-800'}`}>
                    {m.label}
                  </span>
                  <span className="text-[11px] text-slate-500 leading-snug line-clamp-2">
                    {m.description}
                  </span>
                  <div className="flex gap-1.5 mt-0.5">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${SPEED_COLORS[m.speed]}`}>
                      {m.speed}
                    </span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${QUALITY_COLORS[m.quality]}`}>
                      {m.quality}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Imagen de referencia */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowReferenceImage((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <span className="flex items-center gap-2">
            Imagen de referencia
            <span className="text-xs font-normal text-slate-500">(opcional)</span>
            {referenceImageId && (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                Cargada
              </span>
            )}
          </span>
          {showReferenceImage ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </button>
        {showReferenceImage && (
          <div className="p-4 bg-white border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-3">
              Sube una imagen para usarla como base. El modelo la tomara como referencia al generar la nueva imagen.
            </p>
            <ReferenceImageUploader
              currentImageUrl={referenceImageUrl}
              onUploadComplete={({ image_id, public_url }) => {
                setReferenceImageId(image_id)
                setReferenceImageUrl(public_url)
              }}
              onClear={() => {
                setReferenceImageId(null)
                setReferenceImageUrl(null)
              }}
            />
          </div>
        )}
      </div>

      {/* Opciones avanzadas */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvancedOptions((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          Opciones avanzadas
          {showAdvancedOptions ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </button>
        {showAdvancedOptions && (
          <div className="p-4 bg-white border-t border-slate-200 space-y-4">
            <div>
              <label htmlFor="visual-negative-prompt" className="label text-sm">
                Negative prompt (opcional)
              </label>
              <textarea
                id="visual-negative-prompt"
                className="input min-h-[60px]"
                rows={2}
                placeholder="Elementos a excluir: texto, marcas de agua, colores especificos..."
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
              />
            </div>
            <div className="flex items-start gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={useGrounding}
                onClick={() => setUseGrounding((v) => !v)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  useGrounding ? 'bg-emerald-500' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    useGrounding ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <p className="text-sm font-medium text-slate-700">Buscar referencias en Google</p>
                <p className="text-xs text-slate-500">El modelo consulta fuentes actuales para enriquecer el contexto visual antes de generar</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="visual-image-size" className="label text-sm">
                  Resolucion (image size)
                </label>
                <select
                  id="visual-image-size"
                  className="input"
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value)}
                >
                  {IMAGE_SIZES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="visual-thinking-level" className="label text-sm">
                  Nivel de razonamiento (thinking)
                </label>
                <select
                  id="visual-thinking-level"
                  className="input"
                  value={thinkingLevel}
                  onChange={(e) => setThinkingLevel(e.target.value)}
                >
                  {THINKING_LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!excerpt.trim() || isLoading}
          className="btn btn-primary flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Creando...
            </>
          ) : (
            <>
              <ImagePlus className="w-4 h-4" />
              Crear tarea visual
            </>
          )}
        </button>
      </div>
    </form>
  )
}
